"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrderFulfillmentSchema = exports.OrderFulfillmentTypeSchema = exports.WasteEntryInputSchema = exports.WasteLineSchema = exports.WasteReasonSchema = exports.KitchenDealSchema = exports.KitchenDealSlotSchema = exports.MenuPublishedEventSchema = exports.KitchenMenuSchema = exports.KitchenMenuSectionSchema = exports.MenuChannelSchema = exports.SaleLineModifierSchema = exports.KitchenModifierGroupExtSchema = exports.KitchenModifierOptionSchema = exports.ModifierLevelSchema = exports.ModifierPlacementSchema = exports.ModifierBehaviorSchema = exports.KitchenRecipeSchema = exports.KitchenRecipeStepSchema = exports.KitchenRecipeSupplySchema = exports.KitchenSupplyPerMealSizeSchema = exports.KitchenRecipeIngredientSchema = exports.KitchenRecipeSizeSchema = exports.KitchenItemSchema = exports.KitchenUnitLadderSchema = exports.KitchenPackUnitSchema = exports.KitchenItemKindSchema = exports.kitchenItemsProductId = exports.MENU_ITEM_PRODUCT_TYPE_ID = exports.KITCHEN_ITEM_PRODUCT_TYPE_ID = void 0;
const zod_1 = require("zod");
/**
 * Kitchen & Menu contracts (AgenticPOS / RubyTill).
 *
 * One stock system: every kitchen item (ingredient, prep item, supply) is ALSO a
 * variant under the tenant's hidden "Kitchen items" product, so GRN, transfers,
 * counts, the ledger and FIFO cost work on it unchanged. The kitchen record below
 * carries the recipe-facing data (unit ladder, allergens, par) and points at that
 * variant via stockVariantId.
 *
 * All quantities on recipes, modifiers and depletion are in the item's BASE unit
 * (ml, g, each …). Pack units only exist for receiving and counting.
 */
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Product type of the hidden per-tenant product that owns every kitchen variant. */
exports.KITCHEN_ITEM_PRODUCT_TYPE_ID = 'type_ingredient';
/** Product type of sellable menu items (existing). */
exports.MENU_ITEM_PRODUCT_TYPE_ID = 'type_menu_item';
/** Deterministic id of the hidden "Kitchen items" product for a tenant. */
const kitchenItemsProductId = (tenantId) => `kitchen_items_${tenantId}`;
exports.kitchenItemsProductId = kitchenItemsProductId;
// ---------------------------------------------------------------------------
// Kitchen items (ingredient / prep / supply)
// ---------------------------------------------------------------------------
exports.KitchenItemKindSchema = zod_1.z.enum(['ingredient', 'prep', 'supply']);
/** One rung of the unit ladder: `factor` base units per 1 of this pack. */
exports.KitchenPackUnitSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1), // "Case (4 gal)", "gal", "Carton (32 oz)"
    factor: zod_1.z.number().positive(), // base units per pack, e.g. 15141.6 ml
    /** What ONE pack costs (optional). The item's cost per base unit is worked out from these. */
    cost: zod_1.z.number().nonnegative().optional(),
});
exports.KitchenUnitLadderSchema = zod_1.z.object({
    baseUnit: zod_1.z.string().min(1), // "ml" | "g" | "each" | …
    packs: zod_1.z.array(exports.KitchenPackUnitSchema).default([]),
    purchasePackId: zod_1.z.string().optional(), // default pack on GRN
    countPackId: zod_1.z.string().optional(), // default pack on counts
    displayPackId: zod_1.z.string().optional(), // default pack on stock screens
});
exports.KitchenItemSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    kind: exports.KitchenItemKindSchema.default('ingredient'),
    /** @deprecated free text — use categoryId (a product category id, the /pos/categories tree). */
    category: zod_1.z.string().optional(),
    /** Product category id (agenticpos-pos-dynamodb-categories). Optional: unset = Uncategorized. */
    categoryId: zod_1.z.string().max(128).optional(),
    ladder: exports.KitchenUnitLadderSchema,
    /** Latest known cost per BASE unit (FIFO layers are the source of truth per location). */
    costPerBase: zod_1.z.number().nonnegative().default(0),
    stockProductId: zod_1.z.string().optional(),
    stockVariantId: zod_1.z.string().optional(),
    allergens: zod_1.z.array(zod_1.z.string()).default([]),
    /** Par level per location, in base units. */
    par: zod_1.z.record(zod_1.z.string(), zod_1.z.number().nonnegative()).optional(),
    storageArea: zod_1.z.string().optional(), // "Walk-in cooler", "Dry store" …
    /** Stock of this item carries a use-by date (GRN, opening stock, prep batch). */
    tracksExpiry: zod_1.z.boolean().optional(),
    /** Default days from receiving / making to use-by — pre-fills the date, always editable. */
    shelfLifeDays: zod_1.z.number().int().positive().max(3650).optional(),
    shelfOrder: zod_1.z.number().int().nonnegative().optional(),
    imageUrl: zod_1.z.string().optional(),
    supplierIds: zod_1.z.array(zod_1.z.string()).optional(),
    status: zod_1.z.enum(['active', 'archived']).default('active'),
    createdAt: zod_1.z.number().optional(),
    updatedAt: zod_1.z.number().optional(),
});
// ---------------------------------------------------------------------------
// Recipes — compatible with the existing Menu Hub recipe shape
// (steps[].ingredients[].{inventoryItemId, quantityPerSize}) plus optional new fields.
// ---------------------------------------------------------------------------
exports.KitchenRecipeSizeSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    retailPrice: zod_1.z.number().nonnegative().optional(),
});
exports.KitchenRecipeIngredientSchema = zod_1.z.object({
    id: zod_1.z.string().optional(),
    inventoryItemId: zod_1.z.string().min(1),
    quantityPerSize: zod_1.z.record(zod_1.z.string(), zod_1.z.number().nonnegative()).default({}),
});
/** Existing Menu Hub shape: per recipe size, which supply + variation + how many. */
exports.KitchenSupplyPerMealSizeSchema = zod_1.z.object({
    supplyId: zod_1.z.string().min(1),
    supplySizeId: zod_1.z.string().optional(),
    quantity: zod_1.z.number().nonnegative().default(1),
});
exports.KitchenRecipeSupplySchema = zod_1.z.object({
    id: zod_1.z.string().optional(),
    perMealSize: zod_1.z.record(zod_1.z.string(), exports.KitchenSupplyPerMealSizeSchema).optional(),
    // Legacy fields (older recipes)
    supplyId: zod_1.z.string().optional(),
    sizeId: zod_1.z.string().optional(),
    quantity: zod_1.z.number().nonnegative().optional(),
    quantityPerSize: zod_1.z.record(zod_1.z.string(), zod_1.z.number().nonnegative()).optional(),
}).passthrough();
exports.KitchenRecipeStepSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    order: zod_1.z.number().int().nonnegative().default(0),
    description: zod_1.z.string().default(''),
    imageUrl: zod_1.z.string().optional(),
    videoUrl: zod_1.z.string().optional(), // NEW: video per step
    durationSeconds: zod_1.z.number().int().positive().optional(),
    ingredients: zod_1.z.array(exports.KitchenRecipeIngredientSchema).default([]),
    supplies: zod_1.z.array(exports.KitchenRecipeSupplySchema).optional(),
});
exports.KitchenRecipeSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    title: zod_1.z.string().min(1),
    description: zod_1.z.string().default(''),
    categoryId: zod_1.z.string().default(''),
    folderId: zod_1.z.string().optional(),
    imageUrl: zod_1.z.string().optional(),
    videoUrl: zod_1.z.string().optional(),
    sizes: zod_1.z.array(exports.KitchenRecipeSizeSchema).default([]),
    steps: zod_1.z.array(exports.KitchenRecipeStepSchema).default([]),
    status: zod_1.z.enum(['draft', 'published']).default('draft'),
    memo: zod_1.z.string().optional(),
    /** NEW: 'menu' recipes are sold; 'prep' recipes make a kitchen item (batch). */
    kind: zod_1.z.enum(['menu', 'prep']).optional(),
    /** NEW (prep): the kitchen item this batch makes and how much, in base units. */
    yield: zod_1.z.object({ itemId: zod_1.z.string(), quantity: zod_1.z.number().positive() }).optional(),
    stationId: zod_1.z.string().optional(),
    allergens: zod_1.z.array(zod_1.z.string()).optional(),
    version: zod_1.z.number().int().nonnegative().optional(),
    createdBy: zod_1.z.string().optional(),
    createdAt: zod_1.z.number().optional(),
    updatedAt: zod_1.z.number().optional(),
    publishedAt: zod_1.z.number().optional(),
}).passthrough();
// ---------------------------------------------------------------------------
// Modifiers — one model: base | swap | add | remove | note
// ---------------------------------------------------------------------------
exports.ModifierBehaviorSchema = zod_1.z.enum(['base', 'swap', 'add', 'remove', 'note']);
exports.ModifierPlacementSchema = zod_1.z.enum(['whole', 'left', 'right']);
exports.ModifierLevelSchema = zod_1.z.enum(['none', 'light', 'regular', 'extra']);
/**
 * Extension fields for a modifier OPTION. Existing products-service-ext options
 * ({sourceType, ingredientId, supplyId, depletionQty, depletionUnitId, extraPrice})
 * keep working: behavior defaults to 'add', depletionQty/extraPrice are the fallbacks
 * when no per-size value is set.
 */
exports.KitchenModifierOptionSchema = zod_1.z.object({
    optionId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    behavior: exports.ModifierBehaviorSchema.default('add'),
    sourceType: zod_1.z.enum(['ingredient', 'supply', 'custom']).optional(),
    ingredientId: zod_1.z.string().optional(),
    supplyId: zod_1.z.string().optional(),
    /** For swap/remove/base-level: the recipe line (kitchen item id) this option acts on. */
    replacesIngredientId: zod_1.z.string().optional(),
    depletionQty: zod_1.z.number().nonnegative().optional(),
    qtyBySize: zod_1.z.record(zod_1.z.string(), zod_1.z.number().nonnegative()).optional(),
    extraPrice: zod_1.z.number().nonnegative().optional(),
    priceBySize: zod_1.z.record(zod_1.z.string(), zod_1.z.number().nonnegative()).optional(),
    isDefault: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().nonnegative().optional(),
}).passthrough();
exports.KitchenModifierGroupExtSchema = zod_1.z.object({
    /** Pizza-style: each option can go on the whole, left or right half. */
    allowPlacement: zod_1.z.boolean().optional(),
    /** Offer none/light/regular/extra (sauce, cheese). */
    allowLevels: zod_1.z.boolean().optional(),
    /** Allow counts (extra shots ×2). */
    allowCount: zod_1.z.boolean().optional(),
    maxCount: zod_1.z.number().int().positive().optional(),
    /** Half toppings charge half ('half') or full price ('full'). */
    halfPriceRule: zod_1.z.enum(['half', 'full']).optional(),
});
/** A modifier as chosen on a sale line (sent by the till / online store). */
exports.SaleLineModifierSchema = zod_1.z.object({
    groupId: zod_1.z.string().min(1),
    groupName: zod_1.z.string().optional(),
    optionId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    behavior: exports.ModifierBehaviorSchema.default('add'),
    ingredientId: zod_1.z.string().optional(),
    replacesIngredientId: zod_1.z.string().optional(),
    /** Base units for ONE line quantity, already resolved for the size (before count/placement/level). */
    qtyBase: zod_1.z.number().nonnegative().optional(),
    /** Price added for ONE line quantity, already resolved (count/placement/level included). */
    price: zod_1.z.number().nonnegative().default(0),
    count: zod_1.z.number().int().positive().default(1),
    placement: exports.ModifierPlacementSchema.default('whole'),
    level: exports.ModifierLevelSchema.default('regular'),
});
// ---------------------------------------------------------------------------
// Menus (versioned, per location × channel) and the publish event
// ---------------------------------------------------------------------------
exports.MenuChannelSchema = zod_1.z.enum(['pos', 'kiosk', 'online', 'screens', 'doordash', 'table']);
exports.KitchenMenuSectionSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    /** Product ids (menu items or retail products). */
    itemIds: zod_1.z.array(zod_1.z.string()).default([]),
    daypart: zod_1.z.object({ start: zod_1.z.string(), end: zod_1.z.string() }).optional(),
});
exports.KitchenMenuSchema = zod_1.z.object({
    menuId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    locationIds: zod_1.z.array(zod_1.z.string()).default([]),
    channels: zod_1.z.array(exports.MenuChannelSchema).default(['pos']),
    sections: zod_1.z.array(exports.KitchenMenuSectionSchema).default([]),
    schedule: zod_1.z.object({ days: zod_1.z.array(zod_1.z.number().int().min(0).max(6)).optional(), start: zod_1.z.string().optional(), end: zod_1.z.string().optional() }).optional(),
    status: zod_1.z.enum(['draft', 'published', 'archived']).default('draft'),
    /** Latest published version (0 = never published). */
    version: zod_1.z.number().int().nonnegative().default(0),
    draftChangedAt: zod_1.z.number().optional(),
    publishedAt: zod_1.z.number().optional(),
    publishedBy: zod_1.z.string().optional(),
    createdAt: zod_1.z.number().optional(),
    updatedAt: zod_1.z.number().optional(),
});
/** EventBridge detail for `menu.published` (source "agenticpos.kitchen"). */
exports.MenuPublishedEventSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    menuId: zod_1.z.string().min(1),
    version: zod_1.z.number().int().positive(),
    locationIds: zod_1.z.array(zod_1.z.string()),
    channels: zod_1.z.array(exports.MenuChannelSchema),
    publishedAt: zod_1.z.number(),
    publishedBy: zod_1.z.string().optional(),
});
// ---------------------------------------------------------------------------
// Deals (slot combos)
// ---------------------------------------------------------------------------
exports.KitchenDealSlotSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    productIds: zod_1.z.array(zod_1.z.string()).default([]),
    categoryIds: zod_1.z.array(zod_1.z.string()).default([]),
    /** Variant ids allowed (e.g. only Large). Empty = any variant of the products. */
    variantIds: zod_1.z.array(zod_1.z.string()).default([]),
    quantity: zod_1.z.number().int().positive().default(1),
    /** Modifier value included free per unit (e.g. 1 topping). */
    includedModifierValue: zod_1.z.number().nonnegative().default(0),
});
exports.KitchenDealSchema = zod_1.z.object({
    dealId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    price: zod_1.z.number().nonnegative(),
    slots: zod_1.z.array(exports.KitchenDealSlotSchema).min(1),
    channels: zod_1.z.array(exports.MenuChannelSchema).default(['pos']),
    locationIds: zod_1.z.array(zod_1.z.string()).default([]),
    schedule: zod_1.z.object({ days: zod_1.z.array(zod_1.z.number().int().min(0).max(6)).optional(), start: zod_1.z.string().optional(), end: zod_1.z.string().optional() }).optional(),
    stackable: zod_1.z.boolean().default(false),
    status: zod_1.z.enum(['draft', 'active', 'paused']).default('draft'),
    createdAt: zod_1.z.number().optional(),
    updatedAt: zod_1.z.number().optional(),
});
// ---------------------------------------------------------------------------
// Waste log
// ---------------------------------------------------------------------------
exports.WasteReasonSchema = zod_1.z.enum(['waste', 'spoiled', 'expired', 'staff_meal', 'dropped', 'missing', 'found', 'other']);
exports.WasteLineSchema = zod_1.z.object({
    variantId: zod_1.z.string().min(1),
    itemId: zod_1.z.string().optional(),
    quantity: zod_1.z.number().positive(),
    packId: zod_1.z.string().optional(), // unit the user typed in; converted server-side
    baseQuantity: zod_1.z.number().positive().optional(),
});
exports.WasteEntryInputSchema = zod_1.z.object({
    locationId: zod_1.z.string().min(1),
    reason: exports.WasteReasonSchema,
    note: zod_1.z.string().max(500).optional(),
    lines: zod_1.z.array(exports.WasteLineSchema).min(1),
});
// ---------------------------------------------------------------------------
// Order fulfilment (phone / delivery / dine-in) — additive, optional everywhere
// ---------------------------------------------------------------------------
exports.OrderFulfillmentTypeSchema = zod_1.z.enum(['counter', 'pickup', 'delivery', 'dine_in']);
exports.OrderFulfillmentSchema = zod_1.z.object({
    type: exports.OrderFulfillmentTypeSchema,
    channel: zod_1.z.enum(['walk_in', 'phone', 'online', 'kiosk']).optional(),
    customerName: zod_1.z.string().optional(),
    customerPhone: zod_1.z.string().optional(),
    address: zod_1.z.object({
        line1: zod_1.z.string(), line2: zod_1.z.string().optional(), city: zod_1.z.string().optional(),
        state: zod_1.z.string().optional(), zip: zod_1.z.string().optional(),
        lat: zod_1.z.number().optional(), lng: zod_1.z.number().optional(),
    }).optional(),
    driverNote: zod_1.z.string().optional(),
    zoneName: zod_1.z.string().optional(),
    deliveryFee: zod_1.z.number().nonnegative().optional(),
    quotedReadyAt: zod_1.z.number().optional(),
    promisedAt: zod_1.z.number().optional(),
    payAt: zod_1.z.enum(['now', 'door']).optional(),
    /** Restaurant, reserved — not built yet. */
    tableId: zod_1.z.string().optional(),
});
//# sourceMappingURL=kitchen.schema.js.map